import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Sun, Coffee, Utensils, Brain, Moon, Dumbbell, Apple, LineChart } from 'lucide-react';

/**
 * "Your Day with KetoLab" - Timeline Section
 * Shows how the ecosystem integrates into daily life
 * Animations triggered on scroll
 */
const DayWithKetoLab = () => {
  const { t } = useTranslation();
  const [visibleItems, setVisibleItems] = useState([]);
  const containerRef = useRef(null);
  
  const timelineItems = [
    {
      time: '06:00',
      period: 'morning',
      title: t('dayWithKetoLab.morning.title'),
      description: t('dayWithKetoLab.morning.desc'),
      icon: Sun,
      color: '#f97316',
      module: 'KetoScan',
    },
    {
      time: '07:00',
      period: 'morning',
      title: t('dayWithKetoLab.fastingActive.title'),
      description: t('dayWithKetoLab.fastingActive.desc'),
      icon: Coffee,
      color: '#a855f7',
      module: t('dayWithKetoLab.modules.fasting'),
    },
    {
      time: '12:00',
      period: 'afternoon',
      title: t('dayWithKetoLab.optimizedMeal.title'),
      description: t('dayWithKetoLab.optimizedMeal.desc'),
      icon: Utensils,
      color: '#ec4899',
      module: 'AI Chef',
    },
    {
      time: '15:00',
      period: 'afternoon',
      title: t('dayWithKetoLab.smartCoaching.title'),
      description: t('dayWithKetoLab.smartCoaching.desc'),
      icon: Brain,
      color: '#a3e635',
      module: t('dayWithKetoLab.modules.aiCoach'),
    },
    {
      time: '18:00',
      period: 'evening',
      title: t('dayWithKetoLab.workout.title'),
      description: t('dayWithKetoLab.workout.desc'),
      icon: Dumbbell,
      color: '#06b6d4',
      module: t('dayWithKetoLab.modules.challenge8k'),
    },
    {
      time: '20:00',
      period: 'evening',
      title: t('dayWithKetoLab.ketoDinner.title'),
      description: t('dayWithKetoLab.ketoDinner.desc'),
      icon: Apple,
      color: '#22c55e',
      module: 'AI Chef',
    },
    {
      time: '21:00',
      period: 'night',
      title: t('dayWithKetoLab.dayAnalysis.title'),
      description: t('dayWithKetoLab.dayAnalysis.desc'),
      icon: LineChart,
      color: '#eab308',
      module: 'Analytics',
    },
    {
      time: '22:00',
      period: 'night',
      title: t('dayWithKetoLab.fastingStart.title'),
      description: t('dayWithKetoLab.fastingStart.desc'),
      icon: Moon,
      color: '#6366f1',
      module: t('dayWithKetoLab.modules.fasting'),
    },
  ];
  
  // Intersection Observer for scroll animations
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const index = parseInt(entry.target.dataset.index);
            setVisibleItems(prev => [...new Set([...prev, index])]);
          }
        });
      },
      { threshold: 0.3, rootMargin: '-50px' }
    );
    
    const items = containerRef.current?.querySelectorAll('.timeline-item');
    items?.forEach(item => observer.observe(item));
    
    return () => observer.disconnect();
  }, []);
  
  return (
    <div ref={containerRef} className="w-full max-w-4xl mx-auto px-4 py-12">
      {/* Header */}
      <div className="text-center mb-12">
        <h3 className="text-xl sm:text-2xl md:text-3xl font-bold text-white mb-3">
          {t('dayWithKetoLab.header.titlePart1')} <span className="text-[#a3e635]">{t('dayWithKetoLab.header.titleHighlight')}</span> {t('dayWithKetoLab.header.titlePart2')}
        </h3>
        <p className="text-gray-400 text-sm sm:text-base max-w-xl mx-auto">
          {t('dayWithKetoLab.header.subtitle')}
        </p>
      </div>
      
      {/* Timeline */}
      <div className="relative">
        {/* Central line */}
        <div className="absolute left-1/2 top-0 bottom-0 w-0.5 bg-gradient-to-b from-[#a3e635]/50 via-[#a3e635]/30 to-[#a3e635]/50 transform -translate-x-1/2" />
        
        {/* Timeline items */}
        {timelineItems.map((item, index) => {
          const Icon = item.icon;
          const isVisible = visibleItems.includes(index);
          const isLeft = index % 2 === 0;
          
          return (
            <div
              key={index}
              data-index={index}
              className={`timeline-item relative flex items-center mb-8 sm:mb-12
                        ${isLeft ? 'flex-row' : 'flex-row-reverse'}
                        transition-all duration-700
                        ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
              style={{ transitionDelay: `${index * 100}ms` }}
            >
              {/* Content card */}
              <div className={`w-[45%] ${isLeft ? 'pr-8 text-right' : 'pl-8 text-left'}`}>
                <div className={`p-4 rounded-xl bg-black/80 border border-[#a3e635]/30
                              hover:border-[#a3e635]/60 transition-all duration-300
                              shadow-[0_0_15px_rgba(163,230,53,0.1)]
                              hover:shadow-[0_0_25px_rgba(163,230,53,0.2)]`}>
                  {/* Time */}
                  <div className="text-[#a3e635] text-sm font-bold mb-1">{item.time}</div>
                  
                  {/* Title */}
                  <h4 className="text-white font-semibold mb-1">{item.title}</h4>
                  
                  {/* Description */}
                  <p className="text-gray-400 text-xs sm:text-sm leading-relaxed">
                    {item.description}
                  </p>
                  
                  {/* Module badge */}
                  <div className={`inline-flex items-center gap-1 mt-2 px-2 py-1 rounded-full text-xs font-medium
                                bg-black/50 border`}
                       style={{ borderColor: `${item.color}60`, color: item.color }}>
                    <Icon className="w-3 h-3" />
                    {item.module}
                  </div>
                </div>
              </div>
              
              {/* Center icon */}
              <div className="absolute left-1/2 transform -translate-x-1/2 z-10">
                <div 
                  className="w-12 h-12 sm:w-14 sm:h-14 rounded-full flex items-center justify-center
                           bg-black border-2 transition-all duration-300
                           shadow-[0_0_20px_rgba(163,230,53,0.3)]"
                  style={{ 
                    borderColor: item.color,
                    boxShadow: isVisible ? `0 0 25px ${item.color}60` : undefined
                  }}
                >
                  <Icon 
                    className="w-5 h-5 sm:w-6 sm:h-6" 
                    style={{ color: item.color }}
                  />
                </div>
              </div>
              
              {/* Empty space for the other side */}
              <div className="w-[45%]" />
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default DayWithKetoLab;
